import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Submission } from 'types/evaluation';
import TruncatedText from 'components/TruncatedText';
import InformationTab from './InformationTab';
import PresentationTab from './PresentationTab';
import VideoTab from './VideoTab';
import TabNavigation from './TabNavigation';
import NoFinalistsFound from './NoFinalistsFound';

interface FinalistTableProps {
  filteredSubmissions: Submission[];
}

const FinalistTable: React.FC<FinalistTableProps> = ({
  filteredSubmissions,
}) => {
  const [expandedSubmissions, setExpandedSubmissions] = useState<
    Record<string, boolean>
  >({}); // Track which submissions are expanded
  const [activeTab, setActiveTab] = useState<Record<string, string>>({}); // Track active tab for each submission
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  ); // Track loading states for presentations/videos

  // Handle accordion expansion
  const toggleSubmissionExpansion = (
    submissionId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent triggering onSelectSubmission
    setExpandedSubmissions(prev => ({
      ...prev,
      [submissionId]: !prev[submissionId],
    }));

    // Set default tab to 'info' when expanding
    if (!expandedSubmissions[submissionId]) {
      setActiveTab(prev => ({
        ...prev,
        [submissionId]: 'info',
      }));
    }
  };

  // Handle tab switching within accordion
  const handleTabSwitch = (
    submissionId: string,
    tab: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setActiveTab(prev => ({
      ...prev,
      [submissionId]: tab,
    }));

    // Reset loading states when switching tabs
    if (tab === 'presentation' || tab === 'video') {
      setLoadingStates(prev => ({
        ...prev,
        [`${submissionId}_${tab}`]: true,
      }));
    }
  };

  const handleMediaLoad = (submissionId: string, mediaType: string) => {
    setLoadingStates(prev => ({
      ...prev,
      [`${submissionId}_${mediaType}`]: false,
    }));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {filteredSubmissions.length === 0 ? (
        <NoFinalistsFound />
      ) : (
        <table className="w-full table-fixed">
          <thead className="bg-gradient-to-r from-gray-800 to-gray-700">
            <tr>
              <th className="w-6 py-4 px-1"></th>
              <th className="w-12 py-4 px-2 text-center font-baijamjuree font-semibold text-sm text-white border-r border-gray-600">
                No.
              </th>
              <th
                className="text-left py-4 px-3 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '27%' }}
              >
                Project/Profile Name
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '20%' }}
              >
                Submitter
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '18%' }}
              >
                Category
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white border-r border-gray-600"
                style={{ width: '15%' }}
              >
                Pillar
              </th>
              <th
                className="text-left py-4 px-4 font-baijamjuree font-semibold text-sm text-white"
                style={{ width: '14%' }}
              >
                Organization
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredSubmissions.map((submission, index) => {
              const submissionId = String(
                submission.documentId || submission.id
              );
              const isExpanded = expandedSubmissions[submissionId];
              const currentTab = activeTab[submissionId] || 'info';

              // Individual categories that show full name instead of project name
              const individualCategories = [
                'Young Digital Leader Award',
                'Women in Tech Award',
              ];

              const isIndividualCategory = individualCategories.includes(
                submission.category
              );

              const displayName = isIndividualCategory
                ? submission.full_name || submission.description || 'No Name'
                : submission.description ||
                  submission.project_name ||
                  submission.title ||
                  'No Project Name';

              return (
                <React.Fragment key={submissionId}>
                  {/* Table Row */}
                  <tr
                    className={`hover:bg-blue-50 transition-colors duration-200 border-b border-gray-100 cursor-pointer ${
                      isExpanded ? 'bg-blue-50 shadow-sm' : 'bg-white'
                    }`}
                    onClick={e => toggleSubmissionExpansion(submissionId, e)}
                  >
                    <td className="py-4 px-1 align-middle">
                      <div
                        className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'bg-blue-100' : ''}`}
                      >
                        {isExpanded ? (
                          <ChevronDownIcon className="w-4 h-4 text-blue-600" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-2 text-center align-middle border-r border-gray-100">
                      <span className="font-baijamjuree text-sm text-gray-600 font-medium">
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-4 px-3 align-middle border-r border-gray-100">
                      <TruncatedText text={displayName} />
                    </td>
                    <td className="py-4 px-4 align-middle border-r border-gray-100">
                      <TruncatedText
                        text={submission.full_name || 'N/A'}
                        className="text-gray-600"
                      />
                    </td>
                    <td className="py-4 px-4 align-middle border-r border-gray-100">
                      <TruncatedText
                        text={submission.category || 'N/A'}
                        className="text-gray-600"
                      />
                    </td>
                    <td className="py-4 px-4 align-middle border-r border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                        <span className="font-baijamjuree text-sm text-gray-700 break-words leading-tight min-w-0">
                          {submission.pillar === 'Others'
                            ? 'Initiative'
                            : submission.pillar}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 align-middle">
                      <TruncatedText
                        text={submission.org_unit || 'N/A'}
                        className="text-gray-600"
                      />
                    </td>
                  </tr>

                  {/* Accordion Content */}
                  {isExpanded && (
                    <tr>
                      <td
                        colSpan={7}
                        className="p-0 bg-gray-50 border-t border-gray-200"
                      >
                        {/* Review Materials Container */}
                        <div className="bg-white rounded-2xl shadow-smdd-card border border-gray-300 overflow-hidden m-6">
                          <TabNavigation
                            submission={submission}
                            submissionId={submissionId}
                            currentTab={currentTab}
                            handleTabSwitch={handleTabSwitch}
                          />

                          {/* Tab Content */}
                          <div className="p-8">
                            {/* Submission Information Tab */}
                            {currentTab === 'info' && (
                              <InformationTab submission={submission} />
                            )}

                            {/* Presentation Tab */}
                            {currentTab === 'presentation' &&
                              submission.award_material && (
                                <PresentationTab
                                  submission={submission}
                                  submissionId={submissionId}
                                  loadingStates={loadingStates}
                                  handleMediaLoad={handleMediaLoad}
                                />
                              )}

                            {/* Video Tab */}
                            {currentTab === 'video' && submission.video_url && (
                              <VideoTab
                                submission={submission}
                                submissionId={submissionId}
                                loadingStates={loadingStates}
                                handleMediaLoad={handleMediaLoad}
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default FinalistTable;
